/**
 * Plan 10, Task 2.1 — scene factory.
 *
 * `createScene(canvas, config?)` constructs and returns a SceneHandle with:
 *   - a WebGLRenderer mounted on the provided canvas
 *   - a THREE.Scene with a default dark-navy background
 *   - 5 top-level Groups (board, characters, decorations, environment, effects)
 *     pre-attached to the scene
 *   - an idempotent dispose() that tears down the renderer
 *
 * The optional `rendererFactory` on SceneConfig is a TEST-ONLY injection point.
 * Production callers leave it undefined; the factory defaults to
 * `(p) => new THREE.WebGLRenderer(p)`. Do NOT set this in production code.
 */

import * as THREE from 'three';
import type { SceneHandle, SceneGroups } from './types';

export interface SceneConfig {
  /** Background color (THREE.Scene.background). Default: 0x1a1a2e (dark navy). */
  readonly background?: THREE.ColorRepresentation;
  /** WebGLRenderer parameters. Defaults: antialias=true, alpha=false,
   *  powerPreference='high-performance', preserveDrawingBuffer=true.
   *  preserveDrawingBuffer=true is REQUIRED for the visual-smoke fixture
   *  (Task 6.1) to read pixels via canvas.toDataURL() — see
   *  tools/headless-render.js line 47. */
  readonly rendererParams?: THREE.WebGLRendererParameters;
  /**
   * TEST-ONLY injection point. Pass a stub renderer constructor to avoid
   * requiring a real WebGL context in happy-dom / jsdom environments.
   * Production callers MUST leave this undefined.
   */
  readonly rendererFactory?: (params: THREE.WebGLRendererParameters) => THREE.WebGLRenderer;
}

/** Default WebGLRenderer parameters per spec §7 / Task 2.1 plan notes. */
const DEFAULT_RENDERER_PARAMS: THREE.WebGLRendererParameters = {
  antialias: true,
  alpha: false,
  powerPreference: 'high-performance',
  preserveDrawingBuffer: true,
};

/** Default background color: dark navy per plan §Background color. */
const DEFAULT_BACKGROUND: THREE.ColorRepresentation = 0x1a1a2e;

/**
 * Creates a configured Three.js scene with a 5-group hierarchy.
 *
 * @param canvas - The HTMLCanvasElement to render into.
 * @param config - Optional configuration (background, rendererParams,
 *                 rendererFactory). See `SceneConfig` for field docs.
 * @returns A `SceneHandle` owning the renderer, scene, and groups.
 */
export function createScene(canvas: HTMLCanvasElement, config?: SceneConfig): SceneHandle {
  // ── Renderer ───────────────────────────────────────────────────────────────
  const params: THREE.WebGLRendererParameters = {
    ...DEFAULT_RENDERER_PARAMS,
    ...(config?.rendererParams ?? {}),
    canvas,
  };

  const factory =
    config?.rendererFactory ?? ((p: THREE.WebGLRendererParameters) => new THREE.WebGLRenderer(p));

  const renderer = factory(params);
  if ('shadowMap' in renderer) {
    renderer.shadowMap.enabled = true;
  }

  // ── Scene ──────────────────────────────────────────────────────────────────
  const scene = new THREE.Scene();
  const bg = config?.background ?? DEFAULT_BACKGROUND;
  scene.background = new THREE.Color(bg);

  // ── Groups (spec §7 hierarchy) ─────────────────────────────────────────────
  const groups: SceneGroups = {
    board: new THREE.Group(),
    characters: new THREE.Group(),
    decorations: new THREE.Group(),
    environment: new THREE.Group(),
    effects: new THREE.Group(),
  };

  // Attach all groups to the scene in deterministic order.
  scene.add(
    groups.board,
    groups.characters,
    groups.decorations,
    groups.environment,
    groups.effects,
  );

  // ── Dispose (idempotent) ───────────────────────────────────────────────────
  let disposed = false;

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    renderer.dispose();
  }

  // ── Resize (viewport + DPR clamp) ─────────────────────────────────────────

  function onResize(width: number, height: number): void {
    // updateStyle=false: we size the canvas via CSS, not inline style, so
    // the renderer's canvas DOM style is not overwritten on resize.
    renderer.setSize(width, height, false);
    // Clamp DPR to 2: prevents 4× pixel work on high-DPI Retina displays.
    // Use globalThis.devicePixelRatio so test environments can stub it.
    renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio || 1, 2));
  }

  return { renderer, scene, groups, dispose, onResize };
}
